import { BaseService } from './BaseService';
import { DocumentAnnotation, CreateAnnotationPayload, SelectionRect } from '../types';

class AnnotationService extends BaseService {
  constructor() {
    super('/api/annotations');
  }

  async getAnnotationsForDocument(documentId: string): Promise<DocumentAnnotation[]> {
    try {
      const items = await this.get<any[]>(`/document/${documentId}`);
      if (!Array.isArray(items)) return [];
      return items.map(i => this.mapAnnotation(i));
    } catch (error) {
      console.error('AnnotationService.getAnnotationsForDocument error', error);
      return [];
    }
  }

  async createAnnotation(payload: CreateAnnotationPayload): Promise<DocumentAnnotation> {
    const body = {
      documentId: payload.documentId,
      pageNumber: payload.pageNumber,
      noteText: payload.noteText,
      selectedText: payload.selectedText,
      prefixContext: payload.prefixContext || '',
      suffixContext: payload.suffixContext || '',
      startOffset: payload.startOffset || 0,
      endOffset: payload.endOffset || 0,
      rectanglesJson: JSON.stringify(payload.rectangles || []),
      structuralReference: payload.structuralReference || null,
      documentSha256: payload.documentSha256 || null,
      colorHex: payload.colorHex || '#F59E0B',
    };

    const data = await this.post<any>('', body);
    return this.mapAnnotation(data);
  }

  async updateAnnotation(annotationId: string, noteText: string, colorHex?: string): Promise<DocumentAnnotation> {
    const body = {
      noteText,
      colorHex: colorHex || '#F59E0B',
    };

    const data = await this.put<any>(`/${annotationId}`, body);
    return this.mapAnnotation(data);
  }

  async deleteAnnotation(annotationId: string): Promise<boolean> {
    try {
      await this.delete(`/${annotationId}`);
      return true;
    } catch (error) {
      console.error('AnnotationService.deleteAnnotation error', error);
      return false;
    }
  }

  private mapAnnotation(item: any): DocumentAnnotation {
    let parsedRects: SelectionRect[] = [];
    const rawJson = item.rectanglesJson || item.RectanglesJson;

    if (rawJson) {
      try {
        parsedRects = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
      } catch {
        parsedRects = [];
      }
    }

    return {
      id: item.id || item.Id,
      accountId: item.accountId || item.AccountId,
      documentId: String(item.documentId || item.DocumentId),
      pageNumber: Number(item.pageNumber || item.PageNumber || 1),
      noteText: item.noteText || item.NoteText || '',
      colorHex: item.colorHex || item.ColorHex || '#F59E0B',
      selectedText: item.selectedText || item.SelectedText || '',
      prefixContext: item.prefixContext || item.PrefixContext || '',
      suffixContext: item.suffixContext || item.SuffixContext || '',
      startOffset: Number(item.startOffset || item.StartOffset || 0),
      endOffset: Number(item.endOffset || item.EndOffset || 0),
      rectangles: parsedRects,
      structuralReference: item.structuralReference || item.StructuralReference || null,
      documentSha256: item.documentSha256 || item.DocumentSha256 || null,
      createdOn: item.createdOn || item.CreatedOn || '',
      lastModifiedOn: item.lastModifiedOn || item.LastModifiedOn || null,
    };
  }
}

export const annotationService = new AnnotationService();
